import React, { useState, useEffect } from 'react';
import {
    Box,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    TextField,
    Typography,
    Collapse,
    IconButton,
    Card,
    CardContent,
    Grid,
    Chip,
    CircularProgress,
    Divider,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import PaymentIcon from '@mui/icons-material/Payment';
import EventIcon from '@mui/icons-material/Event';
import DescriptionIcon from '@mui/icons-material/Description';
import DeleteIcon from '@mui/icons-material/Delete';
import { getUnpaidUsers, updateUnpaidUser, deleteUnpaidTransactions } from '../api/contractApi';
import { formatCurrency } from '../utils/currency';

/**
 * Overdue: kui on paymentMetadata.periodStart, võrreldakse tähtaega selle kuu
 * ja lepingu paymentDay järgi (mitte ainult tänase kuupäeva päeva numbrit).
 * Kui periodStart või meta puudub → sama mis varem: currentDay > paymentDay.
 */
function computeIsOverdue(row) {
    const contract = row.contract;
    if (!contract?.paymentDay) return false;

    const periodStartRaw = row.paymentMetadata?.periodStart;
    if (periodStartRaw) {
        const periodStart = new Date(periodStartRaw);
        if (!Number.isNaN(periodStart.getTime())) {
            const y = periodStart.getFullYear();
            const m = periodStart.getMonth();
            const lastDayOfMonth = new Date(y, m + 1, 0).getDate();
            const day = Math.min(contract.paymentDay, lastDayOfMonth);
            const dueDate = new Date(y, m, day);
            dueDate.setHours(0, 0, 0, 0);
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            return today > dueDate;
        }
    }

    const currentDay = new Date().getDate();
    return currentDay > contract.paymentDay;
}

// Row component for each unpaid user
const UnpaidUserRow = ({ row, fetchUnpaidUsers, paymentCurrency }) => {
    const [open, setOpen] = useState(false);
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
    const [paidConfirmOpen, setPaidConfirmOpen] = useState(false);
    const [loadingPaid, setLoadingPaid] = useState(false);
    const [loadingDelete, setLoadingDelete] = useState(false);

const isOverdue = computeIsOverdue(row);
const rowCurrency = row.currency || paymentCurrency;
const formatMoney = (value) => formatCurrency(value, rowCurrency);

    const handleDeleteClick = (e) => {
        e.stopPropagation();
        setDeleteConfirmOpen(true);
    };

    const handleDeleteConfirm = async () => {
        setLoadingDelete(true);
        try {
            await deleteUnpaidTransactions(row.id);
            setDeleteConfirmOpen(false);
            fetchUnpaidUsers(); // Refresh the list after deletion
        } catch (error) {
            console.error('Failed to delete transaction:', error);
        } finally {
            setLoadingDelete(false);
        }
    };

    const handleDeleteCancel = () => {
        setDeleteConfirmOpen(false);
    };

    const handlePaidClick = (e) => {
        e.stopPropagation();
        setPaidConfirmOpen(true);
    };

    const handlePaidConfirm = async () => {
        setLoadingPaid(true);
        try {
            await updateUnpaidUser(row.id);
            setPaidConfirmOpen(false);
            fetchUnpaidUsers();
        } catch (error) {
            console.error('Failed to mark as paid:', error);
        } finally {
            setLoadingPaid(false);
        }
    };

    const handlePaidCancel = () => {
        setPaidConfirmOpen(false);
    };

    return (
        <>
            <TableRow
                sx={{
                    '& > *': { borderBottom: 'unset' },
                    backgroundColor: isOverdue ? '#ffdddd' : 'inherit',
                    '&:hover': { backgroundColor: isOverdue ? '#ffcccc' : '#f5f5f5' },
                    cursor: 'pointer'
                }}
                onClick={() => setOpen(!open)}
            >

                <TableCell>{row.user?.fullName || 'N/A'}</TableCell>
                <TableCell>{formatMoney(row.amount)}</TableCell>
                <TableCell>{new Date(row.createdAt).toLocaleDateString()}</TableCell>

                <TableCell>
                    <Button
                        onClick={handlePaidClick}
                        variant="contained"
                        color="success"
                        size="small"
                        disabled={loadingPaid}
                        sx={{
                            borderRadius: 1,
                            textTransform: 'none',
                            boxShadow: 1,
                        }}
                        startIcon={loadingPaid ? <CircularProgress size={16} color="inherit" /> : <PaymentIcon fontSize="small" />}
                    >
                        {loadingPaid ? 'Processing...' : 'Paid'}
                    </Button>
                </TableCell>
            </TableRow>
            <TableRow>
                <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={5}>
                    <Collapse in={open} timeout="auto" unmountOnExit>
                        <Box sx={{ margin: 2 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                                <Typography variant="h6" component="div" sx={{ fontWeight: 'bold' }}>
                                    Detailed Information
                                </Typography>
                                <Button
                                    onClick={handleDeleteClick}
                                    variant="contained"
                                    color="error"
                                    size="small"
                                    disabled={loadingDelete}
                                    sx={{
                                        borderRadius: 1,
                                        textTransform: 'none',
                                        boxShadow: 1,
                                    }}
                                    startIcon={loadingDelete ? <CircularProgress size={16} color="inherit" /> : <DeleteIcon fontSize="small" />}
                                >
                                    {loadingDelete ? 'Deleting...' : 'Delete'}
                                </Button>
                            </Box>

                            <Grid container spacing={3}>
                                <Grid item xs={12} md={6}>
                                    <Card elevation={1} sx={{ height: '100%' }}>
                                        <CardContent>
                                            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', color: '#2c3e50' }}>
                                                User Information
                                            </Typography>
                                            <Divider sx={{ mb: 2 }} />

                                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                                <Typography variant="body1" sx={{ fontWeight: 'bold', mr: 1 }}>
                                                    Name:
                                                </Typography>
                                                <Typography variant="body1">
                                                    {row.user?.fullName || 'N/A'}
                                                </Typography>
                                            </Box>

                                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                                <EmailIcon fontSize="small" sx={{ mr: 1, color: '#3498db' }} />
                                                <Typography variant="body1" sx={{ fontWeight: 'bold', mr: 1 }}>
                                                    Email:
                                                </Typography>
                                                <Typography variant="body1">
                                                    {row.user?.email || 'N/A'}
                                                </Typography>
                                            </Box>

                                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                <PhoneIcon fontSize="small" sx={{ mr: 1, color: '#3498db' }} />
                                                <Typography variant="body1" sx={{ fontWeight: 'bold', mr: 1 }}>
                                                    Phone:
                                                </Typography>
                                                <Typography variant="body1">
                                                    {row.user?.phone || 'N/A'}
                                                </Typography>
                                            </Box>
                                        </CardContent>
                                    </Card>
                                </Grid>

                                <Grid item xs={12} md={6}>
                                    <Card elevation={1} sx={{ height: '100%' }}>
                                        <CardContent>
                                            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', color: '#2c3e50' }}>
                                                <DescriptionIcon sx={{ mr: 1 }} />
                                                Contract Details
                                            </Typography>
                                            <Divider sx={{ mb: 2 }} />

                                            {row.contract ? (
                                                <>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                                        <Typography variant="body1" sx={{ fontWeight: 'bold', mr: 1 }}>
                                                            Contract Type:
                                                        </Typography>
                                                        <Typography variant="body1">
                                                            {row.contract.contractType || 'N/A'}
                                                        </Typography>
                                                    </Box>

                                                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                                        <PaymentIcon fontSize="small" sx={{ mr: 1, color: '#2ecc71' }} />
                                                        <Typography variant="body1" sx={{ fontWeight: 'bold', mr: 1 }}>
                                                            Amount:
                                                        </Typography>
                                                        <Typography variant="body1">
                                                            {formatCurrency(row.contract.paymentAmount, paymentCurrency)}
                                                        </Typography>
                                                    </Box>

                                                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                                        <EventIcon fontSize="small" sx={{ mr: 1, color: '#e74c3c' }} />
                                                        <Typography variant="body1" sx={{ fontWeight: 'bold', mr: 1 }}>
                                                            Payment Day:
                                                        </Typography>
                                                        <Typography variant="body1" sx={{
                                                            color: isOverdue ? '#e74c3c' : 'inherit',
                                                            fontWeight: isOverdue ? 'bold' : 'normal'
                                                        }}>
                                                            {row.contract.paymentDay}
                                                            {isOverdue && " (overdue)"}
                                                        </Typography>
                                                    </Box>

                                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                        <Typography variant="body1" sx={{ fontWeight: 'bold', mr: 1 }}>
                                                            Status:
                                                        </Typography>
                                                        <Chip
                                                            label={row.contract.status}
                                                            color={row.contract.status === "accepted" ? "success" : "default"}
                                                            size="small"
                                                        />
                                                    </Box>
                                                </>
                                            ) : (
                                                <Typography variant="body1">
                                                    No contract information available
                                                </Typography>
                                            )}
                                        </CardContent>
                                    </Card>
                                </Grid>

                                <Grid item xs={12}>
                                    <Card elevation={1}>
                                        <CardContent>
                                            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', color: '#2c3e50' }}>
                                                <PaymentIcon sx={{ mr: 1 }} />
                                                Transaction Details
                                            </Typography>
                                            <Divider sx={{ mb: 2 }} />

                                            <Grid container spacing={2}>
                                                <Grid item xs={12} sm={6}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                                        <Typography variant="body1" sx={{ fontWeight: 'bold', mr: 1 }}>
                                                            Amount:
                                                        </Typography>
                                                        <Typography variant="body1">
                                                           {formatMoney(row.amount)}
                                                        </Typography>
                                                    </Box>

                                                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                                        <Typography variant="body1" sx={{ fontWeight: 'bold', mr: 1 }}>
                                                            Invoice Number:
                                                        </Typography>
                                                        <Typography variant="body1">
                                                            {row.invoiceNumber || 'N/A'}
                                                        </Typography>
                                                    </Box>
                                                </Grid>

                                                <Grid item xs={12} sm={6}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                                        <Typography variant="body1" sx={{ fontWeight: 'bold', mr: 1 }}>
                                                            Payment link sended:
                                                        </Typography>
                                                        <Typography variant="body1">
                                                            {new Date(row.createdAt).toLocaleString()}
                                                        </Typography>
                                                    </Box>

                                                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                                        <Typography variant="body1" sx={{ fontWeight: 'bold', mr: 1 }}>
                                                            Status:
                                                        </Typography>
                                                        <Chip
                                                            label={row.status}
                                                            color="warning"
                                                            size="small"
                                                        />
                                                    </Box>
                                                </Grid>

                                                <Grid item xs={12}>
                                                    <Box sx={{ mt: 1 }}>
                                                        <Typography variant="body1" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                                                            Description:
                                                        </Typography>
                                                        <Typography variant="body1">
                                                            {row.description || 'No description available'}
                                                        </Typography>
                                                    </Box>
                                                </Grid>
                                            </Grid>

                                        </CardContent>
                                    </Card>
                                </Grid>
                            </Grid>
                        </Box>
                    </Collapse>
                </TableCell>
            </TableRow>

            {/* Paid Confirmation Dialog */}
            <Dialog open={paidConfirmOpen} onClose={handlePaidCancel} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ color: 'success.main' }}>Confirm Payment</DialogTitle>
                <DialogContent>
                    <Typography variant="body1">
                        Are you sure you want to mark this payment as paid?
                    </Typography>
                    <Box sx={{ mt: 2, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
                        <Typography variant="body2" color="text.secondary">
                            <strong>User:</strong> {row.user?.fullName || 'N/A'}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            <strong>Amount:</strong> {formatMoney(row.amount)}
                        </Typography>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handlePaidCancel} disabled={loadingPaid}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        color="success"
                        onClick={handlePaidConfirm}
                        disabled={loadingPaid}
                        startIcon={loadingPaid ? <CircularProgress size={16} color="inherit" /> : <PaymentIcon />}
                    >
                        {loadingPaid ? 'Processing...' : 'Mark as Paid'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <Dialog open={deleteConfirmOpen} onClose={handleDeleteCancel} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ color: 'error.main' }}>Confirm Deletion</DialogTitle>
                <DialogContent>
                    <Typography variant="body1">
                        Are you sure you want to delete this transaction?
                    </Typography>
                    <Box sx={{ mt: 2, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
                        <Typography variant="body2" color="text.secondary">
                            <strong>User:</strong> {row.user?.fullName || 'N/A'}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            <strong>Amount:</strong> {formatMoney(row.amount)}
                        </Typography>
                    </Box>
                    <Typography variant="body2" color="error" sx={{ mt: 2 }}>
                        This action cannot be undone.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleDeleteCancel} disabled={loadingDelete}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        color="error"
                        onClick={handleDeleteConfirm}
                        disabled={loadingDelete}
                        startIcon={loadingDelete ? <CircularProgress size={16} color="inherit" /> : <DeleteIcon />}
                    >
                        {loadingDelete ? 'Deleting...' : 'Delete'}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

// Main component
export default function UnpaidUsers({affiliate}) {
    const [unpaidUsers, setUnpaidUsers] = useState([]);
    const [filteredUsers, setFilteredUsers] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [loading, setLoading] = useState(true);
    const paymentCurrency = affiliate?.paymentCurrency || 'EUR';

    // Calculate total unpaid amount
    const totalUnpaidAmount = unpaidUsers.reduce((sum, user) => {
        return sum + (parseFloat(user.amount) || 0);
    }, 0);

    useEffect(() => {
        if (affiliate && affiliate.id) {
            fetchUnpaidUsers();
        }
    }, [affiliate]);

    useEffect(() => {
        filterUsers();
    }, [searchTerm, unpaidUsers]);

    const fetchUnpaidUsers = async () => {
        setLoading(true);
        try {
            // Fetch all unpaid users without search parameter
            const data = await getUnpaidUsers(affiliate.id);
            setUnpaidUsers(data);
            setFilteredUsers(data); // Initial list without filtering
        } catch (error) {
            console.error('Failed to fetch unpaid users:', error);
        } finally {
            setLoading(false);
        }
    };

    // Filtering is now done in the frontend
    const filterUsers = () => {
        if (!searchTerm.trim()) {
            setFilteredUsers(unpaidUsers);
            return;
        }

        const lowerCaseSearch = searchTerm.toLowerCase();
        const filtered = unpaidUsers.filter(item =>
            item.user?.fullName?.toLowerCase().includes(lowerCaseSearch)
        );
        setFilteredUsers(filtered);
    };

    const handleSearchChange = (event) => {
        setSearchTerm(event.target.value);
        // Filtering happens automatically via useEffect
    };

    return (
        <Box sx={{ width: '100%' }}>
            {/* Header with title and total amount */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h5" component="h1" sx={{ fontWeight: 'bold' }}>
                    Unpaid Users
                </Typography>

                <Card elevation={2} sx={{ bgcolor: '#f8f9fa', minWidth: 100 }}>
                    <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <PaymentIcon sx={{ mr: 1, color: '#e74c3c' }} />
                            <Box sx={{ textAlign: 'center' }}>
                                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                                    Total Amount
                                </Typography>
                                <Typography variant="h5" sx={{
                                    fontWeight: 'bold',
                                    color: '#e74c3c',
                                    lineHeight: 1
                                }}>
                                    {formatCurrency(totalUnpaidAmount, paymentCurrency)}
                                </Typography>
                            </Box>
                        </Box>
                    </CardContent>
                </Card>
            </Box>

            <Box sx={{ mb: 3 }}>
                <TextField
                    label="Search by name"
                    variant="outlined"
                    fullWidth
                    value={searchTerm}
                    onChange={handleSearchChange}
                    sx={{ backgroundColor: 'white' }}
                />
            </Box>

            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
                    <CircularProgress />
                </Box>
            ) : (
                <TableContainer component={Paper} sx={{ boxShadow: 2 }}>
                    <Table aria-label="unpaid users table">
                        <TableHead sx={{ backgroundColor: '#f5f5f5' }}>
                            <TableRow>

                                <TableCell sx={{ fontWeight: 'bold' }}>Name</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>Amount</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>Date</TableCell>

                                <TableCell></TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {filteredUsers.length > 0 ? (
                                filteredUsers.map((row) => (
                                    <UnpaidUserRow key={row.id} row={row} fetchUnpaidUsers={fetchUnpaidUsers} paymentCurrency={paymentCurrency} />
                                ))
                            ) : (
                                <TableRow>
                                    <TableCell colSpan={5} align="center" sx={{ py: 3 }}>
                                        {searchTerm ? 'No users matching your search' : 'No unpaid users found'}
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}
        </Box>
    );
}